import { Controller } from '@hotwired/stimulus';
import { debounce, eventListener, observer } from '@inked/utilities';
import { gsap } from 'gsap';

export class MarqueeController extends Controller {
  static targets = ['track'];

  static values = {
    axis: {
      default: 'x',
      type: String,
    },
    speed: {
      default: 50,
      type: Number,
    },
  };

  connect() {
    this.currentElementSize = this.elementSize;

    this.stopMouseOver = eventListener(this.element, 'mouseover', () => this.pause());
    this.stopMouseOut = eventListener(this.element, 'mouseout', () => this.play());
    this.stopResizeObserver = observer(
      this.element,
      'resize',
      debounce(() => {
        if (this.elementSize === this.currentElementSize) return;

        this.reset();
      }),
    );
    this.stopTouchStart = eventListener(this.element, 'touchstart', () => this.pause());
    this.stopTouchEnd = eventListener(this.element, 'touchend', () => this.play());

    this.animate();
  }

  disconnect() {
    if (this.animation) this.animation.kill();
    if (this.stopMouseOver) this.stopMouseOver();
    if (this.stopMouseOut) this.stopMouseOut();
    if (this.stopResizeObserver) this.stopResizeObserver();
    if (this.stopTouchStart) this.stopTouchStart();
    if (this.stopTouchEnd) this.stopTouchEnd();
  }

  animate() {
    const dimension = this.isHorizontal ? 'width' : 'height';

    gsap.set(this.trackTarget, { [this.axisValue]: 0 });

    const totalSize = this.trackTarget.getBoundingClientRect()[dimension];
    const loopSize = totalSize / 2;

    this.animation = gsap.to(this.trackTarget, {
      id: 'scroll',
      [this.axisValue]: -loopSize,
      duration: loopSize / this.speedValue,
      ease: 'none',
      repeat: -1,
    });
  }

  reset() {
    this.currentElementSize = this.elementSize;

    if (this.animation) this.animation.kill();

    this.animate();
  }

  pause() {
    if (
      !this.animation
      || !this.animation.isActive()
    ) return;

    this.animation.pause();
  }

  play() {
    if (
      !this.animation
      || this.animation.isActive()
    ) return;

    this.animation.play();
  }

  get elementSize() {
    return this.isHorizontal
      ? this.element.offsetWidth
      : this.element.offsetHeight;
  }

  get isHorizontal() {
    return this.axisValue === 'x';
  }
}
